import { useEffect, useState } from "react";
import {
  Check,
  Copy,
  FolderPlus,
  Forward,
  ImageOff,
  Download,
  MailCheck,
  Paperclip,
  Reply,
  ReplyAll,
  Trash2,
} from "lucide-react";
import type { MessageDetailDto, Recipient } from "../../../shared/types";
import {
  useAccounts,
  useAssignToVirtualFolder,
  useDeleteMessage,
  useFilters,
  useLabels,
  useMarkRead,
  useMessage,
  useSendMail,
  useVirtualFolders,
} from "../../hooks/useApi";
import { ErrorState } from "../common/ErrorState";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { ComposeDialog } from "./ComposeDialog";
import { bodyDoc, hasRemoteContent } from "./mailContent";
import { splitCombinedId } from "./dragMessage";
import { SWATCH_CLASSES } from "../filterColors";

function formatRecipient(r: Recipient): string {
  return r.name && r.name !== r.address ? `${r.name} <${r.address}>` : r.address;
}

/** "Re: " prefix unless the subject already carries one (incl. Norwegian SV:). */
function replySubject(subject: string): string {
  return /^(re|sv):/i.test(subject.trim()) ? subject : `Re: ${subject}`;
}
function forwardSubject(subject: string): string {
  return /^(fwd?|vs):/i.test(subject.trim()) ? subject : `Fwd: ${subject}`;
}

/** Inner markup of a mail body: full HTML documents can't be nested in a quote. */
function bodyInner(html: string): string {
  const m = /<body[^>]*>([\s\S]*)<\/body>/i.exec(html);
  return m ? m[1]! : html.replace(/<\/?(!doctype|html|head|meta|title)[^>]*>/gi, "");
}

/**
 * The original message, quoted below the typed reply (Gmail-style).
 * Only used for Gmail/IMAP sends — Graph replies keep Exchange's native
 * Outlook-formatted history instead.
 */
function quoted(detail: MessageDetailDto): string {
  const from = detail.from ? `${detail.from.name} <${detail.from.address}>` : "unknown";
  const when = new Date(detail.receivedDateTime).toLocaleString();
  return (
    `<br><br><div style="border-top:1px solid #ccc;padding-top:8px;color:#444">` +
    `<p style="margin:0 0 6px 0;font-size:12px">On ${when}, ${from
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")} wrote:</p>` +
    `<blockquote style="margin:0;padding-left:10px;border-left:2px solid #ccc">${bodyInner(detail.bodyHtml)}</blockquote></div>`
  );
}

interface ComposeSetup {
  title: string;
  to: string;
  cc?: string;
  mode: "reply" | "replyAll" | "forward";
}

function recipientLine(label: string, recipients: Recipient[]) {
  if (!recipients.length) return null;
  return (
    <p className="text-[12px] text-ink-muted">
      {label}: {recipients.map(formatRecipient).join("; ")}
    </p>
  );
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return bytes > 0 ? `${bytes} B` : "";
}


export function ReadingPane({
  accountId,
  messageId,
  onMarkedRead,
  onDeleted,
  onForwarded,
  onCreateFolderFromSender,
  onMovedToFolder,
}: {
  accountId: number;
  messageId: string | null;
  /** Called after "Mark as read" succeeds — the parent closes the mail. */
  onMarkedRead?: () => void;
  /** Called after "Delete mail" succeeds — the parent closes the mail. */
  onDeleted?: () => void;
  /** Called after "Forward to X" succeeds — the parent advances to the next mail. */
  onForwarded?: () => void;
  /** Opens the new-folder dialog prefilled with this sender's address. */
  onCreateFolderFromSender?: (address: string) => void;
  /** Called after "Add to folder" — the parent advances to the next mail. */
  onMovedToFolder?: () => void;
}) {
  const { data, isLoading, error } = useMessage(accountId, messageId);
  const { data: accounts } = useAccounts();
  const markRead = useMarkRead(accountId);
  const deleteMessage = useDeleteMessage(accountId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Sticky preference: delete without the confirmation dialog.
  const [skipConfirm, setSkipConfirm] = useState(
    () => localStorage.getItem("mail.deleteNoConfirm") === "1",
  );
  const toggleSkipConfirm = (checked: boolean) => {
    setSkipConfirm(checked);
    localStorage.setItem("mail.deleteNoConfirm", checked ? "1" : "0");
  };
  const runDelete = () => {
    // Optimistic: the list row is pruned immediately by the hook — close the
    // pane right away; a failure restores the row via refetch and the error
    // shows in the list's error strip.
    setConfirmDelete(false);
    onDeleted?.();
    deleteMessage.mutate(messageId!);
  };
  const [loadRemote, setLoadRemote] = useState(false);
  const [compose, setCompose] = useState<ComposeSetup | null>(null);
  const [copiedAddress, setCopiedAddress] = useState(false);
  // "Add to folder" dropdown: list of virtual folders + "Create new folder…".
  const [folderMenuOpen, setFolderMenuOpen] = useState(false);
  const { data: virtualFolders } = useVirtualFolders(folderMenuOpen);
  const assignToFolder = useAssignToVirtualFolder();
  useEffect(() => {
    if (!folderMenuOpen) return;
    const close = () => setFolderMenuOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [folderMenuOpen]);
  const addToFolder = (folderId: number) => {
    setFolderMenuOpen(false);
    if (!messageId) return;
    assignToFolder.mutate(
      { folderId, messages: [splitCombinedId(messageId, accountId)] },
      { onSuccess: () => onMovedToFolder?.() },
    );
  };
  // Re-block remote content whenever a different message opens.
  useEffect(() => {
    setLoadRemote(false);
    setCompose(null);
    markRead.reset(); // button state is per message
    forwardMail.reset();
    setConfirmDelete(false);
    setCopiedAddress(false);
    setFolderMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  const copyAddress = (address: string) => {
    void navigator.clipboard.writeText(address).then(() => {
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 1500);
    });
  };

  // The account that received this message (combined ids are "acc:raw").
  const replyAccountId =
    accountId !== 0
      ? accountId
      : messageId && messageId.includes(":")
        ? Number(messageId.split(":")[0]) || 0
        : 0;
  const ownAddresses = new Set((accounts ?? []).map((a) => a.username.toLowerCase()));
  // Raw provider id (combined "acc:raw" prefix stripped) for reply/forward threading.
  const rawMessageId =
    accountId === 0 && messageId?.includes(":")
      ? messageId.slice(messageId.indexOf(":") + 1)
      : messageId;

  // "Forward to X": the mail's AI groups that carry a forwarding address.
  const { data: labels } = useLabels(accountId);
  const { data: filters } = useFilters();
  const forwardMail = useSendMail(replyAccountId);
  const matchedFilterIds = (messageId && labels?.[messageId]) || [];
  const forwardTargets = [
    ...new Map(
      (filters ?? [])
        .filter((f) => matchedFilterIds.includes(f.id) && f.email?.trim())
        .map((f) => [f.email!.trim().toLowerCase(), f] as const),
    ).values(),
  ];
  const doForward = (address: string) => {
    if (!data) return;
    forwardMail.mutate(
      {
        to: [address],
        subject: forwardSubject(data.subject),
        body: "",
        quotedHtml: quoted(data),
        replyTo: { messageId: rawMessageId!, mode: "forward" },
      },
      // Forwarded = handled: mark read, then jump to the next mail.
      {
        onSuccess: () =>
          markRead.mutate(messageId!, {
            onSuccess: () => (onForwarded ?? onMarkedRead)?.(),
          }),
      },
    );
  };

  const startReply = (all: boolean) => {
    if (!data) return;
    const to = data.from ? data.from.address : "";
    if (!all) {
      setCompose({ title: "Reply", to, mode: "reply" });
      return;
    }
    // Reply all: original sender + every other recipient, minus our own accounts.
    const others = [...data.toRecipients, ...data.ccRecipients]
      .map((r) => r.address)
      .filter((a) => a && !ownAddresses.has(a.toLowerCase()) && a !== to);
    setCompose({
      title: "Reply all",
      mode: "replyAll",
      to: [to, ...new Set(others.filter((a) => !data.ccRecipients.some((c) => c.address === a)))]
        .filter(Boolean)
        .join("; "),
      cc: [...new Set(data.ccRecipients.map((r) => r.address))]
        .filter((a) => !ownAddresses.has(a.toLowerCase()))
        .join("; "),
    });
  };

  if (!messageId) {
    return (
      <div className="grid flex-1 place-items-center bg-surface">
        <p className="text-[13px] text-ink-muted">Select a message to read it here.</p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="grid flex-1 place-items-center bg-white">
        <p className="text-[13px] text-ink-muted">Loading…</p>
      </div>
    );
  }
  if (error || !data) return <ErrorState error={error ?? new Error("Failed to load message")} />;

  const toolButton =
    "flex items-center gap-1 rounded px-2 py-0.5 text-[12px] font-medium text-accent hover:bg-accent-subtle";

  return (
    <div className="flex min-w-0 flex-1 flex-col bg-white">
      <div className="border-b border-stroke px-5 py-3.5">
        <h2 className="text-[17px] font-semibold">{data.subject}</h2>
        <div className="mt-1.5 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="flex min-w-0 items-center gap-1 text-[13px]">
              <span className="truncate">
                <span className="font-medium">{data.from?.name ?? "Unknown sender"}</span>{" "}
                {data.from && <span className="text-ink-muted">&lt;{data.from.address}&gt;</span>}
              </span>
              {data.from && (
                <button
                  onClick={() => copyAddress(data.from!.address)}
                  className="shrink-0 rounded p-0.5 text-ink-muted hover:bg-black/5 hover:text-ink"
                  title="Copy email address"
                  aria-label="Copy email address"
                >
                  {copiedAddress ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
                </button>
              )}
              {data.from && (
                <span className="relative shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFolderMenuOpen((v) => !v);
                    }}
                    disabled={assignToFolder.isPending}
                    className="flex items-center gap-1 rounded border border-stroke px-1.5 py-0.5 text-[11px] text-ink-muted hover:bg-black/5 hover:text-ink disabled:opacity-50"
                    title="Move this email into a folder"
                  >
                    <FolderPlus size={12} />
                    {assignToFolder.isPending ? "Moving…" : "Add to folder"}
                  </button>
                  {folderMenuOpen && (
                    <span className="absolute left-0 top-full z-30 mt-1 block w-48 rounded border border-stroke bg-white py-1 shadow-lg">
                      {/* Folders scroll; "Create new folder…" stays pinned below. */}
                      <span className="block max-h-56 overflow-y-auto">
                        {(virtualFolders ?? []).map((f) => (
                          <button
                            key={f.id}
                            onClick={() => addToFolder(f.id)}
                            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] hover:bg-black/5"
                          >
                            <span
                              className={`size-2.5 shrink-0 rounded-full ${SWATCH_CLASSES[f.color]}`}
                            />
                            <span className="truncate">{f.name}</span>
                          </button>
                        ))}
                      </span>
                      {onCreateFolderFromSender && (
                        <>
                          {(virtualFolders ?? []).length > 0 && (
                            <span className="my-1 block border-t border-stroke" />
                          )}
                          <button
                            onClick={() => {
                              setFolderMenuOpen(false);
                              onCreateFolderFromSender(data.from!.address);
                            }}
                            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] hover:bg-black/5"
                          >
                            <FolderPlus size={13} className="shrink-0 text-ink-muted" />
                            Create new folder…
                          </button>
                        </>
                      )}
                    </span>
                  )}
                </span>
              )}
            </p>
            {recipientLine("To", data.toRecipients)}
            {recipientLine("Cc", data.ccRecipients)}
          </div>
          {/* Actions right-aligned, Outlook-style, with the timestamp below. */}
          <div className="flex shrink-0 flex-col items-end gap-1">
            <div className="flex items-center gap-0.5">
              <button onClick={() => startReply(false)} className={toolButton} title="Reply to sender">
                <Reply size={14} /> Reply
              </button>
              <button onClick={() => startReply(true)} className={toolButton} title="Reply to everyone">
                <ReplyAll size={14} /> Reply all
              </button>
              <button
                onClick={() => setCompose({ title: "Forward", to: "", mode: "forward" })}
                className={toolButton}
                title="Forward this email"
              >
                <Forward size={14} /> Forward
              </button>
            </div>
            <span className="text-[12px] text-ink-muted">
              {new Date(data.receivedDateTime).toLocaleString()}
            </span>
          </div>
        </div>
        {data.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2.5">
            {data.attachments.map((att) => (
              <a
                key={att.id}
                href={att.url ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open ${att.name}`}
                className="flex items-center gap-1.5 rounded border border-stroke px-2 py-1 text-[12px] text-ink hover:border-accent hover:bg-accent-subtle"
              >
                <Paperclip size={12} className="text-ink-muted" />
                <span className="max-w-[220px] truncate font-medium">{att.name}</span>
                {att.size > 0 && <span className="text-ink-muted">{formatSize(att.size)}</span>}
              </a>
            ))}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center border-b border-stroke px-5 py-1">
        <button
          onClick={() => markRead.mutate(messageId!, { onSuccess: () => onMarkedRead?.() })}
          disabled={markRead.isPending || markRead.isSuccess}
          className={`${toolButton} -ml-2`}
          title="Mark this email as read (here and at the provider)"
        >
          <MailCheck size={14} />
          {markRead.isSuccess ? "Marked as read ✓" : markRead.isPending ? "Marking…" : "Mark as read"}
        </button>
        {data.attachments.length > 0 && (
          <>
            <span className="mx-1.5 h-4 w-px bg-stroke" />
            <button
              onClick={() => {
                // ?download=1 forces Content-Disposition: attachment so even
                // inline-safe types (images, PDFs) save instead of opening.
                for (const att of data.attachments) {
                  if (!att.url) continue;
                  const a = document.createElement("a");
                  a.href = `${att.url}${att.url.includes("?") ? "&" : "?"}download=1`;
                  a.download = att.name;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }
              }}
              className={toolButton}
              title={
                data.attachments.length === 1
                  ? `Download ${data.attachments[0]!.name}`
                  : `Download all ${data.attachments.length} attachments`
              }
            >
              <Download size={14} />
              {data.attachments.length === 1
                ? "Download attachment"
                : `Download ${data.attachments.length} attachments`}
            </button>
          </>
        )}
        <span className="mx-1.5 h-4 w-px bg-stroke" />
        <button
          onClick={() => (skipConfirm ? runDelete() : setConfirmDelete(true))}
          disabled={deleteMessage.isPending}
          className="flex items-center gap-1 rounded px-2 py-0.5 text-[12px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          title="Move this email to Trash"
        >
          <Trash2 size={14} />
          {deleteMessage.isPending ? "Deleting…" : "Delete mail"}
        </button>
        <label className="flex items-center gap-1 text-[12px] text-red-600">
          <input
            type="checkbox"
            checked={skipConfirm}
            onChange={(e) => toggleSkipConfirm(e.target.checked)}
            className="size-3.5 accent-red-600"
          />
          Dont ask for confirmation
        </label>
        <span className="mx-1.5 h-4 w-px bg-stroke" />
        {forwardTargets.map((f) => (
          <button
            key={f.id}
            onClick={() => doForward(f.email!.trim())}
            disabled={forwardMail.isPending || forwardMail.isSuccess}
            className={toolButton}
            title={`Forward this email to ${f.email} (${f.name} group) and mark it read`}
          >
            <Forward size={14} />
            {forwardMail.isSuccess
              ? "Forwarded ✓"
              : forwardMail.isPending
                ? "Forwarding…"
                : `Forward to ${f.email}`}
          </button>
        ))}
        {forwardMail.error && (
          <span className="truncate text-[12px] text-red-700">
            Forward failed: {forwardMail.error.message}
          </span>
        )}
        {deleteMessage.error && (
          <span className="truncate text-[12px] text-red-700">
            Delete failed: {deleteMessage.error.message}
          </span>
        )}
      </div>
      {confirmDelete && (
        <ConfirmDialog
          title="Delete email?"
          message={`"${data.subject}" will be moved to Trash.`}
          busy={deleteMessage.isPending}
          onConfirm={runDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      {hasRemoteContent(data.bodyHtml) && !loadRemote && (
        <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-5 py-1.5 text-[12px] text-amber-800">
          <button
            onClick={() => setLoadRemote(true)}
            className="rounded bg-amber-600 px-2.5 py-0.5 font-medium text-white hover:bg-amber-700"
          >
            Load remote content
          </button>
          <span className="flex flex-1 items-center justify-center gap-1.5">
            <ImageOff size={13} />
            Remote images are blocked to protect your privacy.
          </span>
        </div>
      )}
      {/* Sandboxed iframe: mail HTML runs no scripts and can't touch the app.
          A CSP (see bodyDoc) blocks remote subresources until the user opts in;
          allow-popups lets links open in a new tab (forced via <base>). */}
      <iframe
        key={`${messageId}:${loadRemote}`}
        title="Message body"
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcDoc={bodyDoc(data.bodyHtml, loadRemote)}
        className="min-h-0 w-full flex-1 border-0 bg-white"
      />
      {compose && (
        <ComposeDialog
          accountId={replyAccountId}
          title={compose.title}
          initial={{
            to: compose.to,
            cc: compose.cc,
            subject:
              compose.title === "Forward" ? forwardSubject(data.subject) : replySubject(data.subject),
          }}
          quotedHtml={quoted(data)}
          replyTo={{ messageId: rawMessageId!, mode: compose.mode }}
          onClose={() => setCompose(null)}
        />
      )}
    </div>
  );
}
